<template>
    <div class="main-content">
        <div v-if="!hasData && !loading" class="row">
            <div class="col-12 text-18">
                <b-card class="align-items-center">
                    Nenhum registro encontrado na busca
                </b-card>
            </div>
        </div>
        <div class="row">
            <div class="col-3">
                <card-loader v-if="loading" height="260px" />
                <b-card v-if="hasData && !loading" class="d-flex justify-content-center dash-card">
                    <b-card-title class="mb-1">
                        Pontuação Média
                    </b-card-title>
                    <apexchart
                        v-if="score.length"
                        type="radialBar"
                        :height="180"
                        :options="scoreAverageChart.chartOptions"
                        :series="scoreAverageChart.series"
                    />
                    <!-- <div class="d-flex align-items-center justify-content-center text-18">
                        {{ scoreAverageSubtitle }}
                        <response-emoji
                            :response="scoreAverageResponseEmoji"
                            class="ml-2"
                            size="30px"
                        />
                    </div> -->
                </b-card>
            </div>
            <div class="col-6">
                <card-loader v-if="loading" height="260px" />
                <score-participation-evolution-chart
                    v-else-if="score.length"
                    :dates="dates"
                    :score="score"
                    title="Evolução da Pontuação"
                    class="dash-card"
                />
                <b-card
                    v-else-if="hasData && !loading"
                    class="dash-card"
                >
                    <b-card-title class="mb-2">
                        Evolução da Pontuação
                    </b-card-title>
                </b-card>
            </div>
            <div class="col-3">
                <card-loader v-if="loading" height="260px" />
                <b-card v-if="hasData && !loading" class="d-flex justify-content-center dash-card">
                    <b-card-title>Taxa de Participação</b-card-title>
                    <b-card-body>
                        <div class="mt-3">
                            <b-progress
                                variant="success"
                                height="10px"
                                :value="participationAverage"
                            />
                        </div>
                        <div
                            class="text-center mt-4"
                            style="font-size: 2.5rem; color: cornflowerblue"
                        >
                            {{ participationAverage }}%
                        </div>
                    </b-card-body>
                </b-card>
            </div>
        </div>
        <div class="row mt-3">
            <div class="col-12">
                <card-loader v-if="loading" height="213px" />
                <b-card v-if="hasData && !loading" class="p-3">
                    <div class="row">
                        <div class="col-3 py-3">
                            <vertical-bar-chart
                                variant="info"
                                text="Incrível"
                                :value="veryHappyAverage"
                            />
                        </div>
                        <div class="col-3 py-3">
                            <vertical-bar-chart
                                variant="success"
                                text="Feliz"
                                :value="happyAverage"
                            />
                        </div>
                        <div class="col-3 py-3">
                            <vertical-bar-chart
                                variant="warning"
                                text="Regular"
                                :value="regularAverage"
                            />
                        </div>
                        <div class="col-3 py-3">
                            <vertical-bar-chart
                                variant="danger"
                                text="Infeliz"
                                :value="unhappyAverage"
                            />
                        </div>
                    </div>
                </b-card>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/api/api'
import auth from '@/firebase/auth'

export default {
    name: 'HappinessDashboard',
    data () {
        return {
            loading: false,
            hasData: false,
            dates: [],
            participation: [],
            score: [],
            responses: [],
        }
    },
    components: {
        CardLoader: () => import('@/components/Loaders/CardLoader'),
        // ResponseEmoji: () => import('@/components/Emojis/ResponseEmoji'),
        ScoreParticipationEvolutionChart: () => import('@/components/Charts/ScoreParticipationEvolutionChart'),
        VerticalBarChart: () => import('@/components/Charts/VerticalBarChart'),
    },
    methods: {
        calcResponseAverage (key) {
            const scoreMap = {
                unhappy: 0,
                regular: 1,
                happy: 2,
                veryHappy: 3,
            }
            if (!this.responses.length) return 0
            const amount = this.responses.filter(response => scoreMap[key] === response).length
            const total = this.responses.length
            return +((amount / total) * 100).toFixed(0)
        },
        loadData () {
            this.loading = true
            this.resetData()
            auth
                .currentUser
                .getIdToken()
                .then(token => {
                    api
                        .get('/reports/score-evolution', {
                            params: {
                                range: this.range,
                            },
                            headers: {
                                token,
                            },
                        })
                        .then(response => {
                            response.data = response.data.filter(record => record.allResponses.length)

                            if (!response.data || !response.data.length) {
                                return
                            }

                            this.hasData = true
                            this.score = response.data.map(record => record.average)
                            this.participation = response.data.map(record => record.percentage * 100)
                            this.dates = response.data.map(record => record.dateChart)
                            this.responses = response.data
                                .map(record => record.allResponses)
                                .reduce((acc, cur) => [...acc, ...cur], [])
                        })
                        .finally(() => {
                            this.loading = false
                        })
                })
        },
        resetData () {
            this.hasData = false
            this.dates = []
            this.participation = []
            this.score = []
            this.responses = []
        },
    },
    computed: {
        participationAverage () {
            if (!this.participation || !this.participation.length) return 0

            return +(this.participation.reduce((acc, cur) => acc + cur, 0) / this.participation.length).toFixed(0)
        },
        scoreAverage () {
            if (!this.score || !this.score.length) return 0

            return (this.score.reduce((acc, cur) => acc + cur, 0) / this.score.length).toFixed(0)
        },
        scoreAverageSubtitle () {
            return this.scoreAverage < 25
                ? 'Infeliz'
                : this.scoreAverage >= 25 && this.scoreAverage < 50
                    ? 'Regular'
                    : this.scoreAverage >= 50 && this.scoreAverage < 75
                        ? 'Feliz'
                        : 'Incrível'
        },
        scoreAverageResponseEmoji () {
            return this.scoreAverage < 25
                ? 0
                : this.scoreAverage >= 25 && this.scoreAverage < 50
                    ? 1
                    : this.scoreAverage >= 50 && this.scoreAverage < 75
                        ? 2
                        : 3
        },
        scoreAverageColor () {
            const map = {
                0: '#f44336',
                1: '#ffc107',
                2: '#4caf50',
                3: '#003473',
            }

            return map[this.scoreAverageResponseEmoji]
        },
        scoreAverageChart () {
            if (!this.scoreAverage) return {}

            const average = this.score.reduce((acc, cur) => acc + cur, 0) / this.score.length
            return {
                series: [average],
                chartOptions: {
                    grid: {
                        padding: {
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                        },
                    },
                    chart: {
                        type: 'radialBar',
                    },
                    colors: [this.scoreAverageColor],
                    plotOptions: {
                        radialBar: {
                            hollow: {
                                size: '65%',
                            },
                            dataLabels: {
                                show: true,
                                name: {
                                    show: false,
                                },
                                value: {
                                    formatter: (val) => val.toFixed(0),
                                    fontSize: '2.5rem',
                                    color: 'cornflowerblue',
                                    show: true,
                                },
                            },
                        },
                    },
                    stroke: {
                        lineCap: 'round',
                    },
                },
            }
        },
        unhappyAverage () {
            return this.calcResponseAverage('unhappy')
        },
        regularAverage () {
            return this.calcResponseAverage('regular')
        },
        happyAverage () {
            return this.calcResponseAverage('happy')
        },
        veryHappyAverage () {
            return this.calcResponseAverage('veryHappy')
        },
        range () {
            return this.$store.getters['dashboard/range']
        },
    },
    watch: {
        range: {
            immediate: true,
            handler () {
                this.loadData()
            },
        },
    },
}
</script>

<style lang="scss">
.dash-card {
    height: 270px;
}
</style>
